// ══════════════════════════════════════════════════════════════
// SceneIllustration — Kumon-style SVG illustrations
// Each scene is a small, warm, recognizable picture that
// disambiguates a sentence completion item.
// ══════════════════════════════════════════════════════════════

const COLORS_SCENE = {
  sky:     "#dff0ff",
  water:   "#bfe4ff",
  grass:   "#d6f1c7",
  ground:  "#f5e9d2",
  warm:    "#fff7e8",
  dark:    "#3a3a52",
  outline: "#4a4566",
  accent:  "#ff9a8b",
  yellow:  "#ffd966",
  pink:    "#ffb3c1",
  green:   "#7dc97c",
  orange:  "#ff9e5c",
  brown:   "#b88a5a",
  blue:    "#7cb1ff",
  cream:   "#fff3dc",
};

function SceneFrame({ children, size = 220, bg = COLORS_SCENE.warm }) {
  return (
    <div style={{ width: "100%", maxWidth: size, margin: "0 auto" }}>
      <div style={{ width: "100%", aspectRatio: "1 / 1", background: bg, borderRadius: 24, padding: 16, boxShadow: "0 4px 20px rgba(80,80,140,0.06)" }}>
        <svg viewBox="0 0 200 200" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          {children}
        </svg>
      </div>
    </div>
  );
}

// ── Scenes ──────────────────────────────────────────────────

function FishSwimming() {
  return (
    <SceneFrame bg={COLORS_SCENE.water}>
      {/* water bubbles */}
      <circle cx="40"  cy="40" r="6" fill="#fff" opacity="0.7" />
      <circle cx="160" cy="60" r="4" fill="#fff" opacity="0.7" />
      <circle cx="170" cy="160" r="5" fill="#fff" opacity="0.7" />
      <circle cx="30" cy="170" r="4" fill="#fff" opacity="0.7" />
      {/* fish body */}
      <ellipse cx="100" cy="105" rx="55" ry="34" fill={COLORS_SCENE.orange} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* tail */}
      <polygon points="40,105 15,80 15,130" fill={COLORS_SCENE.orange} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* fin top */}
      <path d="M90 75 Q100 50 120 75 Z" fill={COLORS_SCENE.orange} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* eye */}
      <circle cx="135" cy="95" r="9" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      <circle cx="137" cy="95" r="4.5" fill={COLORS_SCENE.dark}/>
      {/* smile */}
      <path d="M125 118 Q135 125 148 118" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2.5" strokeLinecap="round"/>
      {/* gill */}
      <path d="M115 95 Q120 105 115 115" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2"/>
    </SceneFrame>
  );
}

function DogRunning() {
  return (
    <SceneFrame bg={COLORS_SCENE.grass}>
      {/* ground line */}
      <line x1="10" y1="170" x2="190" y2="170" stroke={COLORS_SCENE.outline} strokeWidth="2" opacity="0.3"/>
      {/* body */}
      <ellipse cx="100" cy="115" rx="55" ry="28" fill={COLORS_SCENE.brown} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* head */}
      <circle cx="150" cy="95" r="28" fill={COLORS_SCENE.brown} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* ear */}
      <path d="M155 70 Q175 65 170 95 Z" fill={COLORS_SCENE.brown} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* eye */}
      <circle cx="158" cy="92" r="3" fill={COLORS_SCENE.dark}/>
      {/* nose */}
      <ellipse cx="174" cy="100" rx="4" ry="3" fill={COLORS_SCENE.dark}/>
      {/* mouth */}
      <path d="M168 108 Q165 113 160 110" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2" strokeLinecap="round"/>
      {/* tongue */}
      <ellipse cx="167" cy="113" rx="4" ry="3" fill={COLORS_SCENE.pink}/>
      {/* legs running */}
      <path d="M70 138 L60 165 L72 165" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3.5" strokeLinecap="round"/>
      <path d="M85 140 L95 165 L85 165" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3.5" strokeLinecap="round"/>
      <path d="M120 140 L120 168" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3.5" strokeLinecap="round"/>
      <path d="M135 140 L145 168" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3.5" strokeLinecap="round"/>
      {/* tail */}
      <path d="M48 108 Q30 95 30 110" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="4" strokeLinecap="round"/>
      {/* motion lines */}
      <line x1="20" y1="90" x2="40" y2="90" stroke={COLORS_SCENE.outline} strokeWidth="2" opacity="0.5"/>
      <line x1="20" y1="120" x2="40" y2="120" stroke={COLORS_SCENE.outline} strokeWidth="2" opacity="0.5"/>
    </SceneFrame>
  );
}

function CatOnMat() {
  return (
    <SceneFrame bg={COLORS_SCENE.warm}>
      {/* mat */}
      <rect x="35" y="135" width="130" height="30" rx="6" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      <line x1="55" y1="135" x2="55" y2="165" stroke={COLORS_SCENE.outline} strokeWidth="1" opacity="0.4"/>
      <line x1="80" y1="135" x2="80" y2="165" stroke={COLORS_SCENE.outline} strokeWidth="1" opacity="0.4"/>
      <line x1="105" y1="135" x2="105" y2="165" stroke={COLORS_SCENE.outline} strokeWidth="1" opacity="0.4"/>
      <line x1="130" y1="135" x2="130" y2="165" stroke={COLORS_SCENE.outline} strokeWidth="1" opacity="0.4"/>
      {/* cat body */}
      <ellipse cx="100" cy="120" rx="40" ry="22" fill="#9aa0b5" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* cat head */}
      <circle cx="100" cy="80" r="28" fill="#9aa0b5" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* ears */}
      <polygon points="78,65 82,40 95,60" fill="#9aa0b5" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      <polygon points="122,65 118,40 105,60" fill="#9aa0b5" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* eyes */}
      <ellipse cx="90"  cy="80" rx="4" ry="6" fill={COLORS_SCENE.dark}/>
      <ellipse cx="110" cy="80" rx="4" ry="6" fill={COLORS_SCENE.dark}/>
      {/* nose */}
      <polygon points="100,90 96,93 104,93" fill={COLORS_SCENE.pink}/>
      {/* mouth */}
      <path d="M100 93 Q95 100 92 97" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      <path d="M100 93 Q105 100 108 97" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* whiskers */}
      <line x1="80" y1="92" x2="65" y2="90" stroke={COLORS_SCENE.outline} strokeWidth="1.5"/>
      <line x1="80" y1="96" x2="65" y2="98" stroke={COLORS_SCENE.outline} strokeWidth="1.5"/>
      <line x1="120" y1="92" x2="135" y2="90" stroke={COLORS_SCENE.outline} strokeWidth="1.5"/>
      <line x1="120" y1="96" x2="135" y2="98" stroke={COLORS_SCENE.outline} strokeWidth="1.5"/>
      {/* tail */}
      <path d="M138 130 Q160 130 158 105" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="6" strokeLinecap="round"/>
      <path d="M138 130 Q160 130 158 105" fill="none" stroke="#9aa0b5" strokeWidth="3" strokeLinecap="round"/>
    </SceneFrame>
  );
}

function FrogHopping() {
  return (
    <SceneFrame bg={COLORS_SCENE.grass}>
      {/* lily pads */}
      <ellipse cx="40" cy="170" rx="26" ry="6" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      <ellipse cx="160" cy="170" rx="26" ry="6" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* hop arc */}
      <path d="M40 165 Q100 30 160 165" fill="none" stroke={COLORS_SCENE.outline} strokeDasharray="5 5" strokeWidth="2" opacity="0.5"/>
      {/* frog body */}
      <ellipse cx="100" cy="100" rx="42" ry="32" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* eye humps */}
      <circle cx="82" cy="78" r="14" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      <circle cx="118" cy="78" r="14" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* eyes */}
      <circle cx="82"  cy="78" r="7" fill="#fff"/>
      <circle cx="118" cy="78" r="7" fill="#fff"/>
      <circle cx="82"  cy="80" r="3.5" fill={COLORS_SCENE.dark}/>
      <circle cx="118" cy="80" r="3.5" fill={COLORS_SCENE.dark}/>
      {/* belly */}
      <ellipse cx="100" cy="110" rx="22" ry="15" fill={COLORS_SCENE.cream}/>
      {/* smile */}
      <path d="M85 110 Q100 122 115 110" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2.5" strokeLinecap="round"/>
      {/* legs */}
      <path d="M62 120 Q50 130 60 145" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3.5" strokeLinecap="round"/>
      <path d="M138 120 Q150 130 140 145" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3.5" strokeLinecap="round"/>
    </SceneFrame>
  );
}

function BeeFlying() {
  return (
    <SceneFrame bg={COLORS_SCENE.sky}>
      {/* motion lines */}
      <path d="M30 60 Q50 50 70 60" fill="none" stroke={COLORS_SCENE.outline} strokeDasharray="4 4" strokeWidth="2" opacity="0.4"/>
      <path d="M30 100 Q50 90 70 100" fill="none" stroke={COLORS_SCENE.outline} strokeDasharray="4 4" strokeWidth="2" opacity="0.4"/>
      {/* body */}
      <ellipse cx="115" cy="100" rx="40" ry="28" fill={COLORS_SCENE.yellow} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* stripes */}
      <path d="M100 75 L100 125" stroke={COLORS_SCENE.outline} strokeWidth="6"/>
      <path d="M125 78 L125 122" stroke={COLORS_SCENE.outline} strokeWidth="6"/>
      {/* wings */}
      <ellipse cx="100" cy="68" rx="20" ry="14" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="2.5" opacity="0.85"/>
      <ellipse cx="125" cy="68" rx="22" ry="16" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="2.5" opacity="0.85"/>
      {/* face */}
      <circle cx="148" cy="98" r="6" fill="#fff"/>
      <circle cx="150" cy="98" r="3" fill={COLORS_SCENE.dark}/>
      <path d="M150 108 Q156 112 160 108" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2" strokeLinecap="round"/>
      {/* antennae */}
      <line x1="142" y1="80" x2="135" y2="65" stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      <circle cx="135" cy="65" r="3" fill={COLORS_SCENE.dark}/>
    </SceneFrame>
  );
}

function DuckSwimming() {
  return (
    <SceneFrame bg={COLORS_SCENE.water}>
      {/* water lines */}
      <path d="M10 145 Q40 140 70 145 T 130 145 T 190 145" fill="none" stroke="#fff" strokeWidth="2" opacity="0.7"/>
      <path d="M10 165 Q40 160 70 165 T 130 165 T 190 165" fill="none" stroke="#fff" strokeWidth="2" opacity="0.7"/>
      {/* body */}
      <ellipse cx="100" cy="115" rx="55" ry="30" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* head */}
      <circle cx="145" cy="80" r="22" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* beak */}
      <polygon points="160,80 180,85 160,90" fill={COLORS_SCENE.orange} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* eye */}
      <circle cx="148" cy="76" r="3" fill={COLORS_SCENE.dark}/>
      {/* tail */}
      <polygon points="50,105 30,95 50,118" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* wing */}
      <path d="M90 100 Q110 95 120 115 Q105 125 85 120 Z" fill={COLORS_SCENE.cream} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
    </SceneFrame>
  );
}

function SunShining() {
  return (
    <SceneFrame bg="#ffefc0">
      {/* rays */}
      {Array.from({ length: 8 }, (_, i) => {
        const a = (i * Math.PI * 2) / 8;
        const x1 = 100 + Math.cos(a) * 50, y1 = 100 + Math.sin(a) * 50;
        const x2 = 100 + Math.cos(a) * 75, y2 = 100 + Math.sin(a) * 75;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={COLORS_SCENE.orange} strokeWidth="6" strokeLinecap="round"/>;
      })}
      {/* sun */}
      <circle cx="100" cy="100" r="42" fill={COLORS_SCENE.yellow} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* face */}
      <circle cx="86" cy="92" r="5" fill={COLORS_SCENE.dark}/>
      <circle cx="114" cy="92" r="5" fill={COLORS_SCENE.dark}/>
      <path d="M82 110 Q100 125 118 110" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3" strokeLinecap="round"/>
      {/* cheeks */}
      <circle cx="78" cy="105" r="4" fill={COLORS_SCENE.pink} opacity="0.7"/>
      <circle cx="122" cy="105" r="4" fill={COLORS_SCENE.pink} opacity="0.7"/>
    </SceneFrame>
  );
}

function PigInMud() {
  return (
    <SceneFrame bg={COLORS_SCENE.warm}>
      {/* mud puddle */}
      <ellipse cx="100" cy="155" rx="80" ry="20" fill="#7a5a3c" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* body */}
      <ellipse cx="100" cy="115" rx="50" ry="32" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* head */}
      <circle cx="148" cy="100" r="25" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* snout */}
      <ellipse cx="170" cy="105" rx="12" ry="9" fill="#ffc6d5" stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      <circle cx="167" cy="105" r="2" fill={COLORS_SCENE.dark}/>
      <circle cx="173" cy="105" r="2" fill={COLORS_SCENE.dark}/>
      {/* eye */}
      <circle cx="150" cy="95" r="3" fill={COLORS_SCENE.dark}/>
      {/* ears */}
      <polygon points="138,80 130,68 145,75" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      {/* legs in mud */}
      <rect x="65" y="135" width="10" height="20" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      <rect x="120" y="135" width="10" height="20" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* tail */}
      <path d="M50 105 Q35 100 40 90 Q50 88 45 95" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
    </SceneFrame>
  );
}

function ChildInBed() {
  return (
    <SceneFrame bg="#e8e0ff">
      {/* bed frame */}
      <rect x="30" y="120" width="140" height="50" rx="6" fill={COLORS_SCENE.brown} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* pillow */}
      <rect x="40" y="105" width="50" height="22" rx="6" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      {/* blanket */}
      <rect x="30" y="135" width="140" height="35" rx="4" fill={COLORS_SCENE.blue} stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      <line x1="60" y1="135" x2="60" y2="170" stroke={COLORS_SCENE.outline} strokeWidth="1" opacity="0.4"/>
      <line x1="100" y1="135" x2="100" y2="170" stroke={COLORS_SCENE.outline} strokeWidth="1" opacity="0.4"/>
      <line x1="140" y1="135" x2="140" y2="170" stroke={COLORS_SCENE.outline} strokeWidth="1" opacity="0.4"/>
      {/* head on pillow */}
      <circle cx="65" cy="100" r="20" fill="#ffd9b8" stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      {/* hair tuft */}
      <path d="M50 90 Q55 75 70 78 Q72 82 70 88 Z" fill={COLORS_SCENE.brown} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* sleepy eye */}
      <path d="M58 100 Q63 103 68 100" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2" strokeLinecap="round"/>
      <path d="M73 100 Q78 103 80 100" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="1.5" strokeLinecap="round"/>
      {/* zzz */}
      <text x="120" y="80" fontSize="22" fontWeight="900" fill={COLORS_SCENE.outline} opacity="0.8">z</text>
      <text x="135" y="65" fontSize="18" fontWeight="900" fill={COLORS_SCENE.outline} opacity="0.6">z</text>
      <text x="148" y="50" fontSize="14" fontWeight="900" fill={COLORS_SCENE.outline} opacity="0.4">z</text>
    </SceneFrame>
  );
}

function CupSteaming() {
  return (
    <SceneFrame bg={COLORS_SCENE.warm}>
      {/* steam */}
      <path d="M85 50 Q80 35 90 25 Q100 35 95 45" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2.5" opacity="0.5" strokeLinecap="round"/>
      <path d="M105 50 Q110 35 100 25 Q90 35 95 45" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2.5" opacity="0.5" strokeLinecap="round"/>
      <path d="M115 50 Q110 35 120 25 Q130 35 125 45" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2.5" opacity="0.5" strokeLinecap="round"/>
      {/* cup body */}
      <path d="M65 75 L75 165 Q75 175 85 175 L130 175 Q140 175 140 165 L150 75 Z" fill={COLORS_SCENE.blue} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* cup top opening */}
      <ellipse cx="107" cy="75" rx="42" ry="10" fill="#a3cdff" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* handle */}
      <path d="M150 95 Q175 95 175 130 Q175 155 150 150" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      <path d="M150 105 Q165 105 165 130 Q165 145 150 142" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
    </SceneFrame>
  );
}

function ShipSailing() {
  return (
    <SceneFrame bg={COLORS_SCENE.water}>
      {/* waves */}
      <path d="M10 140 Q40 130 70 140 T 130 140 T 190 140" fill="none" stroke="#fff" strokeWidth="2.5" opacity="0.9"/>
      <path d="M10 160 Q40 150 70 160 T 130 160 T 190 160" fill="none" stroke="#fff" strokeWidth="2.5" opacity="0.7"/>
      {/* hull */}
      <path d="M40 130 L160 130 L145 165 L55 165 Z" fill={COLORS_SCENE.brown} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* mast */}
      <line x1="100" y1="130" x2="100" y2="40" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* sail */}
      <path d="M100 45 L100 120 L150 120 Z" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      <path d="M100 45 L100 120 L60 120 Z" fill={COLORS_SCENE.cream} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* flag */}
      <polygon points="100,40 115,45 100,50" fill={COLORS_SCENE.accent} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
    </SceneFrame>
  );
}

function FlagWaving() {
  return (
    <SceneFrame bg={COLORS_SCENE.sky}>
      {/* pole */}
      <line x1="60" y1="40" x2="60" y2="180" stroke={COLORS_SCENE.outline} strokeWidth="5" strokeLinecap="round"/>
      <circle cx="60" cy="38" r="5" fill={COLORS_SCENE.yellow} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* flag */}
      <path d="M60 50 L160 45 Q170 75 160 105 L60 100 Z" fill={COLORS_SCENE.accent} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* fold */}
      <path d="M120 50 Q115 75 120 100" fill="none" stroke={COLORS_SCENE.outline} strokeWidth="2" opacity="0.5"/>
      {/* ground */}
      <line x1="20" y1="180" x2="180" y2="180" stroke={COLORS_SCENE.outline} strokeWidth="2" opacity="0.3"/>
    </SceneFrame>
  );
}

function CakeOnPlate() {
  return (
    <SceneFrame bg={COLORS_SCENE.warm}>
      {/* plate */}
      <ellipse cx="100" cy="160" rx="80" ry="12" fill="#e0e0ec" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* cake bottom layer */}
      <rect x="50" y="115" width="100" height="35" rx="4" fill={COLORS_SCENE.cream} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* frosting drip */}
      <path d="M50 115 Q60 125 70 115 Q80 125 90 115 Q100 125 110 115 Q120 125 130 115 Q140 125 150 115" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      {/* top layer */}
      <rect x="65" y="80" width="70" height="30" rx="3" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* candle */}
      <rect x="97" y="55" width="6" height="22" fill={COLORS_SCENE.blue} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* flame */}
      <path d="M100 55 Q95 45 100 35 Q105 45 100 55 Z" fill={COLORS_SCENE.orange} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* sprinkles */}
      <circle cx="80" cy="95" r="2.5" fill={COLORS_SCENE.yellow}/>
      <circle cx="120" cy="98" r="2.5" fill={COLORS_SCENE.green}/>
      <circle cx="100" cy="92" r="2.5" fill={COLORS_SCENE.blue}/>
    </SceneFrame>
  );
}

function MudPuddle() {
  return PigInMud(); // alias
}

function TreeWithFruit() {
  return (
    <SceneFrame bg={COLORS_SCENE.sky}>
      {/* trunk */}
      <rect x="92" y="100" width="16" height="70" fill={COLORS_SCENE.brown} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* canopy */}
      <circle cx="100" cy="80" r="50" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      <circle cx="75" cy="65" r="22" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      <circle cx="125" cy="65" r="22" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      {/* apples */}
      <circle cx="78" cy="78" r="6" fill={COLORS_SCENE.accent} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      <circle cx="100" cy="100" r="6" fill={COLORS_SCENE.accent} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      <circle cx="125" cy="85" r="6" fill={COLORS_SCENE.accent} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* ground */}
      <line x1="20" y1="170" x2="180" y2="170" stroke={COLORS_SCENE.outline} strokeWidth="2"/>
    </SceneFrame>
  );
}

function MoonNight() {
  return (
    <SceneFrame bg="#1f1d4a">
      {/* stars */}
      <circle cx="40" cy="40" r="2" fill="#fff"/>
      <circle cx="160" cy="35" r="2.5" fill="#fff"/>
      <circle cx="50" cy="100" r="1.5" fill="#fff"/>
      <circle cx="170" cy="120" r="2" fill="#fff"/>
      <circle cx="30" cy="160" r="1.5" fill="#fff"/>
      <circle cx="150" cy="170" r="2" fill="#fff"/>
      {/* moon (crescent) */}
      <circle cx="100" cy="100" r="40" fill="#fff8d0"/>
      <circle cx="115" cy="95" r="34" fill="#1f1d4a"/>
      {/* face on visible part */}
      <circle cx="83" cy="92" r="3" fill={COLORS_SCENE.dark}/>
      <path d="M75 110 Q82 118 90 112" fill="none" stroke={COLORS_SCENE.dark} strokeWidth="2" strokeLinecap="round"/>
    </SceneFrame>
  );
}

function FlowerBloom() {
  return (
    <SceneFrame bg={COLORS_SCENE.sky}>
      {/* stem */}
      <line x1="100" y1="100" x2="100" y2="170" stroke={COLORS_SCENE.green} strokeWidth="5" strokeLinecap="round"/>
      {/* leaf */}
      <ellipse cx="80" cy="130" rx="14" ry="6" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="2" transform="rotate(-30 80 130)"/>
      {/* petals */}
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <ellipse key={angle} cx="100" cy="70" rx="14" ry="22" fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="2" transform={`rotate(${angle} 100 100)`}/>
      ))}
      {/* center */}
      <circle cx="100" cy="100" r="15" fill={COLORS_SCENE.yellow} stroke={COLORS_SCENE.outline} strokeWidth="2.5"/>
      {/* ground */}
      <line x1="20" y1="170" x2="180" y2="170" stroke={COLORS_SCENE.outline} strokeWidth="2"/>
    </SceneFrame>
  );
}

function BookOpen() {
  return (
    <SceneFrame bg={COLORS_SCENE.warm}>
      {/* book pages */}
      <path d="M30 60 L100 50 L100 160 L30 170 Z" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      <path d="M170 60 L100 50 L100 160 L170 170 Z" fill="#fff" stroke={COLORS_SCENE.outline} strokeWidth="3"/>
      {/* spine line */}
      <line x1="100" y1="50" x2="100" y2="160" stroke={COLORS_SCENE.outline} strokeWidth="2"/>
      {/* text lines */}
      <line x1="40" y1="80" x2="92" y2="73" stroke={COLORS_SCENE.outline} strokeWidth="1.5" opacity="0.6"/>
      <line x1="40" y1="95" x2="92" y2="88" stroke={COLORS_SCENE.outline} strokeWidth="1.5" opacity="0.6"/>
      <line x1="40" y1="110" x2="80" y2="104" stroke={COLORS_SCENE.outline} strokeWidth="1.5" opacity="0.6"/>
      <line x1="40" y1="125" x2="92" y2="118" stroke={COLORS_SCENE.outline} strokeWidth="1.5" opacity="0.6"/>
      <line x1="108" y1="73" x2="160" y2="80" stroke={COLORS_SCENE.outline} strokeWidth="1.5" opacity="0.6"/>
      <line x1="108" y1="88" x2="160" y2="95" stroke={COLORS_SCENE.outline} strokeWidth="1.5" opacity="0.6"/>
      <line x1="108" y1="104" x2="150" y2="110" stroke={COLORS_SCENE.outline} strokeWidth="1.5" opacity="0.6"/>
      <line x1="108" y1="118" x2="160" y2="125" stroke={COLORS_SCENE.outline} strokeWidth="1.5" opacity="0.6"/>
    </SceneFrame>
  );
}

// ── Counted objects scene (for word problems) ──
function CountedObjects({ objectType = "apple", count = 3 }) {
  const positions = [];
  const cols = Math.min(count, 5);
  const rows = Math.ceil(count / cols);
  for (let i = 0; i < count; i++) {
    const r = Math.floor(i / cols);
    const c = i % cols;
    positions.push({ x: 50 + c * 30 + (cols < 5 ? (5 - cols) * 15 : 0), y: 70 + r * 35 });
  }
  return (
    <SceneFrame bg={COLORS_SCENE.warm}>
      {positions.map((p, i) => <ObjectIcon key={i} type={objectType} cx={p.x} cy={p.y}/>)}
    </SceneFrame>
  );
}

function ObjectIcon({ type, cx, cy }) {
  if (type === "apple" || type === "apples") {
    return (
      <g>
        <circle cx={cx} cy={cy} r="11" fill={COLORS_SCENE.accent} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
        <path d={`M${cx} ${cy-12} L${cx} ${cy-15}`} stroke={COLORS_SCENE.outline} strokeWidth="2" strokeLinecap="round"/>
        <path d={`M${cx} ${cy-14} Q${cx+5} ${cy-18} ${cx+8} ${cy-14}`} fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="1.5"/>
      </g>
    );
  }
  if (type === "shells") {
    return (
      <g>
        <path d={`M${cx-10} ${cy+8} Q${cx-12} ${cy-8} ${cx} ${cy-10} Q${cx+12} ${cy-8} ${cx+10} ${cy+8} Z`} fill={COLORS_SCENE.pink} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
        <path d={`M${cx-5} ${cy+5} L${cx} ${cy-8}`} stroke={COLORS_SCENE.outline} strokeWidth="1.5"/>
        <path d={`M${cx+5} ${cy+5} L${cx} ${cy-8}`} stroke={COLORS_SCENE.outline} strokeWidth="1.5"/>
      </g>
    );
  }
  if (type === "stars") {
    const star = (cx, cy) => {
      const pts = [];
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? 11 : 5;
        const a = (Math.PI * 2 * i) / 10 - Math.PI / 2;
        pts.push(`${cx + Math.cos(a) * r},${cy + Math.sin(a) * r}`);
      }
      return pts.join(" ");
    };
    return <polygon points={star(cx, cy)} fill={COLORS_SCENE.yellow} stroke={COLORS_SCENE.outline} strokeWidth="2"/>;
  }
  if (type === "frogs") {
    return (
      <g>
        <ellipse cx={cx} cy={cy} rx="11" ry="8" fill={COLORS_SCENE.green} stroke={COLORS_SCENE.outline} strokeWidth="2"/>
        <circle cx={cx-4} cy={cy-5} r="3" fill="#fff"/>
        <circle cx={cx+4} cy={cy-5} r="3" fill="#fff"/>
        <circle cx={cx-4} cy={cy-5} r="1.5" fill={COLORS_SCENE.dark}/>
        <circle cx={cx+4} cy={cy-5} r="1.5" fill={COLORS_SCENE.dark}/>
      </g>
    );
  }
  // default: dot
  return <circle cx={cx} cy={cy} r="9" fill={COLORS_SCENE.blue} stroke={COLORS_SCENE.outline} strokeWidth="2"/>;
}

const SCENES = {
  fish_swimming: FishSwimming,
  dog_running:   DogRunning,
  cat_on_mat:    CatOnMat,
  frog_hopping:  FrogHopping,
  bee_flying:    BeeFlying,
  duck_swimming: DuckSwimming,
  sun_shining:   SunShining,
  pig_in_mud:    PigInMud,
  child_in_bed:  ChildInBed,
  cup_steaming:  CupSteaming,
  ship_sailing:  ShipSailing,
  flag_waving:   FlagWaving,
  cake_on_plate: CakeOnPlate,
  mud_puddle:    MudPuddle,
  tree_with_fruit: TreeWithFruit,
  moon_night:    MoonNight,
  flower_bloom:  FlowerBloom,
  book_open:     BookOpen,
};

// ── Emoji fallback scene ──
// When a sentence doesn't have a hand-drawn SVG scene, pick a single large
// emoji that matches the target word and frame it the same way. This gives
// 100% illustration coverage without 50+ SVG components.
const EMOJI_BY_TEMPLATE_WORD = {
  // verbs (actions)
  // "ride" intentionally has NO entry (MA-121c): its only served template,
  // CS_12_064_v2 "I can ride this ___", has correctAnswer "bike" — mapping
  // ride→🚲 rendered the answer's own emoji even though the matched WORD
  // differed from "bike", an emoji-identity leak the word-only guard missed.
  swim: "🏊", run: "🏃", jump: "🤸", hop: "🐰",
  sit: "🪑", stand: "🧍", walk: "🚶", read: "📖",
  write: "✏️", draw: "🎨", sing: "🎤", dance: "💃", eat: "🍽️",
  drink: "🥤", play: "🎮", help: "🤝", buzz: "🐝", bark: "🐶",
  // verb-tense aliases (MA-121b) — same emoji as their base form above.
  ran: "🏃", ate: "🍽️", sat: "🪑", swam: "🏊",
  // animals (verbs/sounds about them) — buzz/bee, moo/cow etc intentionally
  // share an emoji (a sound word's subject-animal scene); NOT the ride/bike
  // leak class, since ride was a GENERIC verb aliased to one specific noun,
  // while these are onomatopoeia uniquely tied to their one animal.
  cow: "🐮", moo: "🐮", sheep: "🐑", baa: "🐑", owl: "🦉", hoot: "🦉",
  snake: "🐍", slither: "🐍", mouse: "🐭", squeak: "🐭", rabbit: "🐰",
  // MA-592 emoji-map slice (2026-09-13): sleep 🐻 → 😴. The bear glyph was
  // a hibernation stand-in that named an ANIMAL, not the action; its only
  // corpus consumer was encodingWords HFW_EXP3_sc_030 "I will ___ in my
  // bed." (no controlledSentences item has sleep as target or in-template
  // word — measured), where a bear steered a 5yo away from the key. 😴 is
  // the glyph this file already uses for nap/rest.
  lion: "🦁", roar: "🦁", bear: "🐻", sleep: "😴", elephant: "🐘",
  whale: "🐋", monkey: "🐵", horse: "🐴", chicken: "🐔",
  // body
  eyes: "👀", ears: "👂", nose: "👃", feet: "🦶", hands: "👐",
  legs: "🦵", tongue: "👅", mouth: "👄",
  // food / drinks
  water: "💧", milk: "🥛", honey: "🍯", wheat: "🌾", apple: "🍎",
  apples: "🍎", grape: "🍇", banana: "🍌", carrot: "🥕", egg: "🥚",
  fork: "🍴", spoon: "🥄", knife: "🔪", bread: "🍞", pizza: "🍕",
  ham: "🍖", snack: "🍪", cup: "🥤",
  // adjectives — MODIFIER_TIER_WORDS below (MA-121a); still real (if
  // weaker) scene depictions, kept but ranked BELOW concrete nouns/
  // animals/food so a modifier appearing earlier in a template no longer
  // outranks a concrete noun appearing later (the 🐭-for-"wren" class).
  hot: "🔥", cold: "❄️", sweet: "🍬", sour: "🍋",
  dark: "🌑", small: "🐭",
  slow: "🐢", fast: "⚡", happy: "😊",
  sad: "😢", full: "🍱", empty: "🪣",
  // MA-121a: "nest" joins MODIFIER_TIER_WORDS below (not dropped, not
  // top tier) — see the comment there for why.
  nest: "🪺",
  // nature / weather
  snow: "❄️", rain: "🌧️", wind: "💨", cloud: "☁️", star: "⭐",
  sand: "🏖️",
  // time
  day: "☀️", night: "🌙", days: "📅", months: "📅", year: "📅",
  // location / things
  pen: "✏️", car: "🚗", bike: "🚲", house: "🏠", school: "🏫",
  hive: "🐝", key: "🔑", ball: "⚽", boat: "⛵",
  hill: "⛰️", mountain: "🏔️", flower: "🌸", flowers: "🌷",
  fire: "🔥", door: "🚪", window: "🪟", chair: "🪑", table: "🪑",
  ship: "🚢", shop: "🏪", bag: "🎒",
  log: "🪵", box: "📦", bus: "🚌", flag: "🚩", hat: "🧢",
  // MA-121a: SYMBOL_TIER_WORDS below (pure color/direction/affirmation
  // symbols — 🔴💚💙⚪💛⬆️⬇️⬅️➡️✅❌ — carry no SCENE meaning for a 5yo).
  // Kept as an absolute LAST RESORT (never preferred over anything else)
  // rather than removed outright, so a template where one is the ONLY
  // match still gets a picture instead of none (see the comment there).
  red: "🔴", green: "💚", blue: "💙", white: "⚪", yellow: "💛",
  up: "⬆️", down: "⬇️", left: "⬅️", right: "➡️", yes: "✅", no: "❌",
  // misc
  bird: "🐦", cat: "🐱", dog: "🐶", fish: "🐟", duck: "🦆",
  pig: "🐷", frog: "🐸", bee: "🐝", bug: "🐛", web: "🕸️",
  // NOTE (MA-121b, deliberately NOT added): mast, dock, tent, sun, pan,
  // cake, pond — audited against the real served corpus and EXCLUDED.
  // Each one's served occurrences collapse the answer via common-sense
  // association rather than merely depicting a subject: "The ___ had a
  // tall mast" (ans "ship", opts ship/fish/dish) or "A ___ can drift on a
  // pond" (ans "raft"/"frog"/"duck"/"fish" across its 9 served items) make
  // the answer obvious from the picture alone, bypassing the decoding
  // skill the item is meant to test — a stronger leak than literal word/
  // emoji identity, and NOT caught by either safety net. Left open for a
  // future per-item content-review pass (not a map-only fix).
  //
  // NOTE (MA-121a, "pen" DEFERRED, not fixed, 2026-07-20): "pen" serves 7
  // real occurrences, 5 of them the animal-ENCLOSURE sense ("Dad fed the
  // ___ in the pen") rendering a pencil ✏️ — a wrong-referent scene, only
  // 2/7 use the writing-pen sense. Ranking CANNOT fix this (verified: in
  // all 7 templates "pen" is the ONLY matched word — there is no
  // competing signal to prefer), so the only map-only fix is outright
  // removal — but every one of these 7 templates has NO other matching
  // word, so removing "pen" turns all 7 into a newly-EMPTY PIC hint,
  // which collides with `readingHintContentAudit.mjs`'s (MA-118) frozen
  // seed — a SEPARATE guard whose explicit invariant is "a NEW zero-info/
  // empty hint can never be grandfathered." Rather than force that
  // cross-guard exception into this ranking/symbol-drop slice, "pen" is
  // LEFT AS-IS (same behavior as pre-MA-121a) — same treatment as mast/
  // dock/pond above: a known, documented, deferred defect, not a
  // map-only fix. A future slice can pair the "pen" removal with the
  // matching readingHintContentAuditSeed.json/Baseline.json update in
  // ONE coordinated diff (or wait for MA-119's mapper-level fix). Also
  // dropped (unlike "pen", zero corpus impact — no served item goes
  // empty): "huge" (0 served occurrences, aliased the same emoji as
  // "elephant" — a latent emoji-identity leak waiting for a future item);
  // "fly" (only 1 served occurrence, and a WRONG word-sense match — "A
  // small biting fly is a ___" uses "fly" as the insect NOUN, but the
  // map's "fly" key was for the VERB sense, rendering a dove 🕊️ for an
  // insect item; that template's "small" still matches via
  // MODIFIER_TIER_WORDS, so dropping "fly" costs nothing).
};

// Modifier-tier words (MA-121a): real but WEAKER scene depictions — an
// adjective standing in for a color/size/temperature (not a concrete
// noun/animal/food the sentence is actually about), OR "nest" (a concrete
// noun, but one whose only served common-sense-collapse occurrence,
// CS_15_032 "A small ___ built a nest in the bush" ans "wren" opts
// wren/wrap/wrong, would otherwise let "nest" win outright over "small"
// under ranking and make "wren" the obvious pick via world-knowledge — the
// SAME collapse class as mast/dock/pond. Placing "nest" here instead of
// top-tier makes "small" (which appears first in that one template) win
// the tier instead, matching PRE-MA-121a behavior for that item exactly
// (no new leak introduced), while the other 2 served "nest" occurrences
// — where no modifier word competes — still render 🪺 as a harmless
// subject depiction via this same tier. Ranked below every top-tier key
// so a modifier/nest word appearing earlier in a template no longer
// outranks a concrete noun appearing later (the 🐭-for-"wren" class).
const MODIFIER_TIER_WORDS = new Set([
  "hot", "cold", "sweet", "sour", "dark", "small",
  "slow", "fast", "happy", "sad", "full", "empty", "nest",
]);

// Symbol-tier words (MA-121a): pure color/direction/affirmation symbols
// that carry NO scene meaning for a 5yo (a red circle, an arrow, a
// checkmark — never a thing in the story). Ranked BELOW modifier-tier
// too — used only as an absolute last resort, when NO other word in the
// template matches anything else at all. This is deliberately a
// last-resort tier rather than outright removal: removing these keys
// entirely would turn every template where one is the ONLY match into a
// newly-empty PIC hint, colliding with `readingHintContentAudit.mjs`'s
// (MA-118) frozen seed (see the long NOTE above re: "pen", which hits
// this exact issue and is deferred instead). Keeping them as a last
// resort achieves the item's actual ask — never preferred over a
// concrete noun or even a modifier — without that cross-guard collision.
const SYMBOL_TIER_WORDS = new Set([
  "red", "green", "blue", "white", "yellow",
  "up", "down", "left", "right", "yes", "no",
]);

// Pick the WORD (and its emoji) that drives the scene, ranked (MA-121a) in
// three tiers: a concrete-noun/animal/food/verb match (anything NOT in
// MODIFIER_TIER_WORDS or SYMBOL_TIER_WORDS) wins outright and is returned
// immediately, even if a lower-tier word appears earlier in the template.
// Only when NO top-tier word matches does the first MODIFIER_TIER_WORDS
// match (if any) get used; only when NEITHER a top- nor modifier-tier word
// matches does the first SYMBOL_TIER_WORDS match (if any) get used. This is
// what fixes e.g. "small" (🐭) or "up" (⬆️) winning over a later concrete
// noun purely by appearing first in the sentence.
//
// Exported (not just the emoji) so callers that need to know WHICH word
// drove the picture — e.g. the answer-safety guard — can use the real
// ranking outcome directly, instead of re-deriving it by reverse-searching
// "which word's solo lookup equals this emoji." A reverse search is UNSAFE
// under ranking: a lower-tier word can share an emoji with the true
// higher-tier winner (e.g. hot/fire both → 🔥) and appear earlier in the
// template, so a naive `find` would misattribute the picture to the
// lower-tier word even though the higher-tier word is what actually won.
export function pickEmojiWordFromTemplate(template) {
  const templateWords = String(template).toLowerCase().match(/\b\w+\b/g) || [];
  let modifierMatch = null;
  let symbolMatch = null;
  for (const word of templateWords) {
    const emoji = EMOJI_BY_TEMPLATE_WORD[word];
    if (!emoji) continue;
    if (!MODIFIER_TIER_WORDS.has(word) && !SYMBOL_TIER_WORDS.has(word)) return { word, emoji };
    if (MODIFIER_TIER_WORDS.has(word) && modifierMatch === null) modifierMatch = { word, emoji };
    if (SYMBOL_TIER_WORDS.has(word) && symbolMatch === null) symbolMatch = { word, emoji };
  }
  return modifierMatch || symbolMatch;
}

// Pick an emoji from the SUBJECT of the template (a noun the child can
// already see in the prompt). Kernel principle 11: the visual must not
// depend on the answer. If no subject match exists, render no emoji —
// the prompt itself is the scaffold and the visual stays empty.
//
// EXCEPTION (MA-308, mode (a) only): decoding-cloze items (controlledSentences)
// deliberately invert this for their OWN target word — see pickTargetEmoji
// below and depictionMode on the default export. This function's own
// bystander-scan behavior is UNCHANGED; the exception lives entirely in a
// separate, explicitly-opted-in codepath.
export function pickEmojiFromTemplate(template) {
  return pickEmojiWordFromTemplate(template)?.emoji ?? null;
}

// MA-308 slice 2 (full-target-coverage, partial): a map consulted ONLY by
// pickTargetEmoji below — NEVER by pickEmojiFromTemplate/pickEmojiWordFromTemplate
// (the mode (b) bystander scan). This separation is load-bearing, not cosmetic:
// several words below (starting with "cake") are permanently forbidden from
// EMOJI_BY_TEMPLATE_WORD (see its "audited-and-rejected" comment + the
// sceneIllustrationEmojiSafety.test.mjs pins for cake/pond/mast/dock/tent/sun/pan)
// because adding them THERE would let the mode (b) bystander scan match them in
// an unrelated item and leak a DIFFERENT item's answer via common-sense
// association (concretely: 5 real controlledSentences items have
// correctAnswer:"bake" with "cake" visibly in the template — if "cake" were
// in the shared map, a mode-(b)-style scan of those templates would render
// 🎂, giving away "bake" via world-knowledge). Mode (a) never scans a
// template for a bystander — pickTargetEmoji does a DIRECT lookup on the
// item's OWN correctAnswer only — so this risk cannot occur here, and a word
// can safely be listed below even though it must stay out of the shared map.
//
// Seeded from a real-corpus coverage audit (2026-07-22): every launch-safe
// controlledSentences word below was checked against EVERY real served
// occurrence for (1) a single, unambiguous, depictable sense and (2) zero
// target/distractor emoji collisions within its own item (both verified by
// the real-corpus sweep in sceneIllustrationEmojiSafety.test.mjs). Two
// candidates found during that audit — "ring" (3-of-8 real occurrences are
// the bell-sound VERB sense, e.g. "The bell can ___" — 💍 jewelry would
// misdepict those) and "cap" (1-of-4 is the pen-cap sense, e.g. "The pen
// has a ___" — 🧢 headwear would misdepict it) — were DELIBERATELY EXCLUDED
// rather than mapped: unlike the shared map's modifier/symbol tiers, this
// map has no per-item override, so a wrong-sense minority occurrence can't
// be special-cased, and a decoding item's picture actively misleading a
// child (vs. merely absent) is worse than the status quo. Left unmapped —
// a future slice could add a per-item (not per-word) override if needed.
const TARGET_ONLY_EMOJI_BY_WORD = {
  lamp: "💡", cake: "🎂", lake: "🏞️", home: "🏠", bone: "🦴",
  mule: "🫏", kid: "🧒", shed: "🏚️", dish: "🥣",
  king: "🤴", wing: "🪽", bed: "🛏️", pup: "🐶", bun: "🍞",
  mom: "👩", gift: "🎁", pot: "🍲", map: "🗺️", van: "🚐",
  hand: "✋", rock: "🪨", tub: "🛁", clap: "👏", sled: "🛷",
  plant: "🪴", sip: "🥤",
  // MA-308 full-target-coverage slice 3 (2026-07-22): 25-word batch from a
  // real-corpus audit + converged design/red-team pass. "chop" was audited
  // and DROPPED (not added) — 7/8 real occurrences are wood-chopping with
  // an axe, but CS_11_055 ("The cook will ___ the ham") is a food-prep
  // context where an axe is a real-world-implausible depiction and this
  // map has no per-item override — the same defect class that sank
  // "ring"/"cap" in slice 2. Clean single-sense words: knot, sign, fox,
  // gem, eight, hut, drum, man, knit, sun, wrong, pond, smell. Disclosed
  // approximations/residues (flagged for the Cowork live-QA visual check,
  // same tier as the already-shipped mule/shed/wing): "jar"/"comb" use
  // newer Unicode 15.0/14.0 glyphs unverified in this headless env; "sigh"
  // is a ZWJ ("face exhaling") sequence, same risk tier; "wasp" borrows
  // bee's emoji (no dedicated wasp glyph exists in Unicode) and "lamb"
  // borrows sheep's (no baby-sheep glyph exists); "tag" ("Tim can tag the
  // bag") and "nut" ("Jan put the nut on a log") have one occurrence each
  // that is plausibly ambiguous but with no textual proof of an alternate
  // sense (does not clear the ring/cap evidentiary bar); "jazz" (sax is a
  // genre stereotype, not literal), "numb" (reuses cold/snow's ❄️ — depicts
  // the cause, not the sensation), "dim" (reuses dark's 🌑), "design"
  // (reuses draw's 🎨), and "light" (1/4 occurrence, "light the lamps at
  // dusk", uses the verb/ignite sense vs. the noun/illumination sense in
  // the other 3 — judged the same concept depicted correctly either way,
  // same tier as the already-accepted "plant" verb/noun residue) are all
  // real-but-workable approximations, not sense-collapses. "sun" and
  // "pond" reuse "day"'s/"lake"'s emoji despite those words' bystander-scan
  // exclusion from EMOJI_BY_TEMPLATE_WORD — safe here because
  // TARGET_ONLY_EMOJI_BY_WORD is never consulted by the bystander scan
  // (see the design note above), verified by a full corpus collision sweep
  // (0 collisions, before and after this batch).
  tag: "🏷️", knot: "🪢", sign: "🪧", fox: "🦊", gem: "💎",
  eight: "8️⃣", hut: "🛖", jar: "🫙", comb: "🪮", drum: "🥁",
  man: "👨", sigh: "😮‍💨", nut: "🥜", jazz: "🎷", knit: "🧶",
  sun: "☀️", wrong: "❌", numb: "❄️", dim: "🌑", design: "🎨",
  pond: "🏞️", light: "💡", smell: "👃", wasp: "🐝", lamb: "🐑",
  // MA-308 full-target-coverage slice 4 (2026-07-22, redone against fresh
  // main after PR #932's attempt closed on a merge conflict, not a content
  // defect): 24-word batch from an independent design pass + an adversarial
  // red-team re-audit of every real corpus occurrence. The red-team REJECTED
  // 6 design candidates for the exact wrong-sense-minority defect class that
  // sank "ring"/"cap"/"chop" in slices 2-3 — do not re-add these without a
  // per-item override mechanism: "glass" (CS_14_068 tests the MATERIAL sense
  // — "The cup was made of clear ___" — a glass-of-milk depiction misdepicts
  // it), "wrap" (CS_15_035 "___ the sandwich in foil" — a gift-box emoji
  // misdepicts a foil-wrapped sandwich), "thumb" (CS_15_090 "She hit her
  // ___ on the door" — a thumbs-up gesture emoji directly contradicts an
  // injury context), "gnaw" (🦷 depicts the tooth, not the gnawing action —
  // no established idiomatic tie the way a wrench reads as "fix"), "plum"
  // (🍑 is peach, not plum, plus a well-known adult slang connotation —
  // brand-safety reject independent of fruit accuracy), "cute" (🥰 is a
  // reaction/adoration face, not a depiction — category mismatch, sharpest
  // on CS_12_090 "My red hat looks ___" where the picture shows a face, not
  // a hat). Clean single-sense words shipped: name, time, math, hour, fizz,
  // grin, step, skunk, camp, tent, fog. Disclosed approximations (verb/noun
  // applied to a varying specific object, or no exact glyph exists — same
  // tier as the already-accepted plant/mule/shed/wasp/lamb residues, not a
  // sense-split): "bake" (bread stands in for oven-cooking ham/yam/cake/
  // bread/buns/pie), "chip" (🍟 fries — no dedicated potato-chip glyph),
  // "rich" (💰 money-bag for the wealth sense, all 8 occurrences consistent),
  // "lift"/"hum"/"fix" (established generic action-symbols: bicep/note/
  // wrench), "gnat" (🦟 mosquito — both tiny biting flying insects, no
  // dedicated gnat glyph), "knee" (🦵 — knee's own 3 occurrences are single-
  // sense; unlike "leg" itself, which stays excluded for its genuine bed-leg
  // furniture sense-split, CS_06_062), "raft" (🛶 canoe), "mop" (🧹 broom —
  // both read as generic cleaning tools), "jam"/"gum" (🍯/🍬 — jarred/
  // wrapped sweets, no dedicated glyph, verified 0 in-item collision), "wet"
  // (💧 — both occurrences are the damp/soaked sense). Full independent
  // corpus re-verification (every occurrence read, not sampled) + a
  // mechanical collision sweep (0 real collisions) backs every entry below.
  name: "📛", time: "🕐", bake: "🍞", chip: "🍟", rich: "💰",
  math: "🔢", hour: "⏰", lift: "💪", hum: "🎵", gnat: "🦟",
  fizz: "🫧", knee: "🦵", raft: "🛶", mop: "🧹", jam: "🍯",
  gum: "🍬", grin: "😁", step: "👣", skunk: "🦨", camp: "🏕️",
  tent: "⛺", fix: "🔧", wet: "💧", fog: "🌫️",
  // MA-308 full-target-coverage slice 5 (2026-07-22): a 23-word batch from
  // an independent design pass (28 candidates) + an adversarial red-team
  // re-audit of every real corpus occurrence, lifting real mode-a coverage
  // 565->613 (measured). The red-team REJECTED 5 design candidates for the
  // exact wrong-sense-minority / wrong-object-depiction defect class that
  // sank "ring"/"cap"/"chop"/"glass"/"wrap"/"thumb" in prior slices — do
  // not re-add these without a per-item override mechanism: "doll" (🪆 is
  // a solid one-piece nesting doll; all 3/3 real occurrences describe a
  // cloth doll that WEARS a wig/hat, which 🪆 physically cannot depict —
  // contradicted, not just a minority miss), "ride"/"rode" (🚲 bicycle
  // directly contradicts 3/19 occurrences that explicitly name a different
  // vehicle/animal — "___ the bus", "___ in the car", "___ the mule at the
  // farm" — the same bike-collision risk this word was ALREADY excluded
  // from in the shared bystander map, reintroduced here), "tube" (🧴 squeeze
  // lotion bottle contradicts 2/8 occurrences describing a rigid mailing/
  // storage tube — "can hold sand", "the map was in the tube"), "band" (no
  // dedicated wristband glyph exists; 🎗️ is the awareness-ribbon emoji,
  // which carries real-world advocacy connotation unrelated to "held the
  // band on his arm" and is visually nothing like a wristband). Clean
  // single-sense words shipped: bell, ghost, pill, call, hug, nap, rat,
  // sink, draft, knock, job. Disclosed approximations (generic action-
  // symbol or reused/generic-species glyph — same tier as the already-
  // accepted bake/raft/mop/lift/hum/fix/wasp/lamb residues, not a sense-
  // split): "climb" (🧗 generic climbing-person symbol), "vest" (🦺 safety-
  // vest glyph stands in for a rain/travel vest — garment category is
  // right), "hen" (🐔 generic chicken glyph, no dedicated hen glyph),
  // "sprint" (🏃 reuses the generic running-person glyph), "pass" (⚽
  // object-for-action metaphor — a soccer ball for "pass the ball with her
  // foot", both real occurrences are the identical sentence), "pan" (🍳 egg-
  // in-pan glyph for a bun/ham-in-pan context — correct object, incidental
  // content mismatch), "clip" (📎 paperclip stands in for a bag-clip — no
  // dedicated bag-clip glyph), "wren" (🐦 generic bird glyph — no dedicated
  // wren glyph exists in Unicode; verified both real occurrences are the
  // literal small-bird sense), "den" (🕳️ generic hole glyph — verified
  // accurate: both real occurrences are literal animal-burrow contexts),
  // "swat" (🏏 cricket-bat glyph is the only bat+ball glyph in Unicode;
  // both real occurrences literally pair "ball" with "bat" — flagged for
  // Cowork live-QA visual check, same disclosure tier as jar/comb/sigh),
  // "colt" (🐴 generic horse glyph, no dedicated baby-horse glyph — same
  // tier as lamb->sheep), "dig" (⛏️ tool-for-action metaphor, same tier as
  // fix->wrench). Full independent corpus re-verification (every occurrence
  // read by both the design and red-team passes, not sampled) + a
  // mechanical collision sweep (0 real collisions) backs every entry below.
  bell: "🔔", ghost: "👻", climb: "🧗", pill: "💊", call: "📞",
  hug: "🤗", nap: "😴", rat: "🐀", hen: "🐔", vest: "🦺",
  sprint: "🏃", pass: "⚽", pan: "🍳", sink: "🚰", clip: "📎",
  draft: "💨", wren: "🐦", den: "🕳️", knock: "👊", swat: "🏏",
  colt: "🐴", job: "💼", dig: "⛏️",
  // MA-308 full-target-coverage slice 6 (2026-07-22): a 21-word batch from an
  // independent design pass (25 candidates) + an adversarial red-team
  // re-audit of every real corpus occurrence, lifting real mode-a coverage
  // 613->634 (measured). The red-team REJECTED 4 design candidates for real
  // defects: "twig" (🪵 is the `log` glyph — a substantial log directly
  // contradicts BOTH real occurrences' own stated reasoning for the answer,
  // "small enough to hold in a hand/grip" — the picture contradicts the text
  // it illustrates, the same class that sank "thumb"/"glass"), "spill" (💦
  // sweat-droplets carries a well-known adult/sexual slang connotation in
  // emoji culture — a brand-safety reject independent of literal accuracy,
  // same class as "plum"; a connotation-free alternative, 💧, already exists
  // for the identical splashing-liquid concept via "drip"), "hit"/"bang"
  // (both real occurrences of each are literal and single-sense, but the
  // only available depiction, 💥, is a comic-book collision/explosion glyph —
  // more visceral than this map's usual neutral tool-for-action metaphors
  // (fix->wrench, dig->pickaxe) for a violence-sensitive children's app;
  // held back pending a founder gut-check on tone rather than shipped on a
  // content-accuracy pass alone). "mask" was ACCEPTED but with a corrected
  // emoji from the design pass's first choice: CS_09_045 ("A mask can cover
  // a face") is a generic riddle with no illness context, so 😷 (Unicode's
  // medical/illness face-mask glyph) is a real content mismatch — 🎭
  // (performing-arts masks) is the clean available fix for the generic
  // disguise/costume sense the clue actually implies. Clean single-sense
  // words shipped: crack, drip, cod, puff, bright, truck, sock, bin, yam,
  // pin, wash, melt. Disclosed approximations (part-for-whole, generic-
  // species/prototype glyph, or reused glyph within the same semantic
  // family — same tier as the already-accepted bake/raft/mop/hen/wren
  // residues, not a sense-split): "stick" (🪵 log-glyph approximates a
  // thinner stick, unlike "twig" above this is NOT contradicted by either
  // real occurrence's own text), "pack" (🎒 backpack glyph spans the noun
  // "backpack" and verb "pack his shirt for camp" senses — both read as
  // camp-gear-adjacent, same tier as the already-accepted plant/light
  // verb-noun residue), "crust"/"slab" (🍞 bread glyph, part-for-whole, same
  // tier as bake->bread/chip->fries), "jug" (🫙 jar-shape glyph approximates
  // a jug's handle+neck, same tier as raft->canoe/mop->broom), "fluff"
  // (☁️ cloud glyph for a soft-fluffy-texture metaphor, same tier as
  // numb->❄️; minor disclosed color mismatch — the corpus says "soft yellow
  // fluff", ☁️ renders white, flagged for the Cowork live-QA visual check),
  // "cliff" (🏔️ mountain-glyph landform approximation, same tier as the
  // already-shipped lake->🏞️/shed->🏚️ residues), "toy" (🧸 teddy-bear
  // glyph, prototypical-instance-for-category, same tier as hen/wren/colt).
  // Full independent corpus re-verification (every occurrence read by both
  // the design and red-team passes, not sampled) + a mechanical collision
  // sweep (0 real collisions, incl. against same-family reused glyphs like
  // 🍞/🪵/🎒/🐟/🫙/💨/🏔️) backs every entry below.
  crack: "🕳️", stick: "🪵", pack: "🎒", drip: "💧", cod: "🐟",
  crust: "🍞", slab: "🍞", jug: "🫙", puff: "💨", fluff: "☁️",
  cliff: "🏔️", bright: "✨", truck: "🚚", sock: "🧦", mask: "🎭",
  toy: "🧸", bin: "🗑️", yam: "🍠", pin: "🧷", wash: "🧼",
  melt: "🫠",

  // MA-308 full-target-coverage slice 7 (2026-07-22): a smaller 5-word batch
  // — the uncovered-word frequency tail left after 6 rounds skews heavily
  // toward function words (much, that, with, went — no concrete referent)
  // and real sense-splits (made, line, bump, cut, stamp, cab, print, drop,
  // snap — see the rejects below), so this slice's accept rate is lower
  // than prior rounds. An independent design pass + an adversarial
  // red-team re-audit of every real occurrence (both confirmed via a full
  // mechanical collision sweep against the merged map) converged on:
  // "knight" (🛡️, symbol-for-concept, same tier as job->💼/sign->🪧),
  // "dab" (🖌️, tool-for-action, same tier as fix->🔧/dig->⛏️), "pat" (✋,
  // reuses hand's existing glyph, body-part-for-action, same tier as
  // lift->💪). Disclosed approximations (part-for-whole/reused-glyph
  // residues, same tier as the already-accepted jug/stick residues, not a
  // sense-split — flagged for the Cowork live-QA visual check): "lid"
  // (🫙 reuses jar's glyph — every real occurrence is a generic-cover sense
  // across box/jar/pot/can, the tested attribute is "a lid covers", not
  // container shape) and "stump" (🪵 reuses stick's log glyph — a stump's
  // canonical orientation differs from a fallen log, but neither real
  // occurrence states a contradicting attribute the way "twig"'s did).
  // Real mode-a coverage lifts 646->660 (measured). Rejected this slice
  // (see the map's own "sense-split" precedent from chop/glass/thumb):
  // "made" (spans cooking/writing/crafting with no single tool-icon
  // covering all 9 real occurrences), "line" (genuine 3-way split: queue
  // vs. drawn line vs. line of text), "bump" (3-way split: sound-effect vs.
  // collision-verb vs. swelling-noun, the last also injury-sensitive),
  // "cut" (wound-noun vs. slicing-verb split, wound sense also injury-
  // sensitive), "stamp" (postage-stamp-noun vs. foot-stamping-verb split),
  // "cab" (truck-cab vs. taxi-cab split, confirmed via CS_06_016/017's
  // explicit taxi context), "print" (paw-print-noun vs. handwriting-verb
  // split), "drop" (droplet-noun vs. disposal-verb split), "snap"
  // (sound-effect-noun vs. container-closing-verb split, neither is the
  // finger-snap gesture). Also rejected: pure abstract function words
  // (much, that, with — no concrete referent) and words with no adequate
  // Unicode glyph that clears the no-abstract-swatch bar (chin, mud, rug,
  // mat, belt, wig, spot, peg, gnome, plan, thin, long, honest, flat,
  // went, pink, fan, rag, press, skip, fold, calm, hem) — see the full
  // audit trail in the BACKLOG.md decision log for this slice.
  knight: "🛡️", dab: "🖌️", pat: "✋", lid: "🫙", stump: "🪵",

  // MA-308 full-target-coverage slice 8 (2026-07-23): a 23-word batch, the
  // freq-2 tail plus a freq-1 concrete-noun sweep. Clean single-sense
  // matches: "bob" (proper name, same tier as kid/man/mom), "dress",
  // "hammer", "mug", "ram" (direct glyph matches). Reused-glyph
  // approximations (same "prototype/part-for-whole" tier as crust/slab/
  // knee->leg): "bud" (seedling for flower bud, color mismatch disclosed
  // same as fluff), "crumb" (bread for crumb), "limb" (log/stick/stump's
  // wood glyph for tree branch — both real occurrences literally define
  // limb as branch), "pull" (lift's bicep glyph for the physical-pulling
  // action), "rim" (mug's own cup glyph for a vessel rim; zero collision
  // with this same slice's separate "mug" entry — different items),
  // "wring" (wringing water depicted by its result, a water drop),
  // "wrist" (hand's glyph — no dedicated wrist glyph exists, adjacent-
  // body-part approximation same as pat), "hall" (school-building glyph
  // for a school hallway — no dedicated hallway glyph, same tier as
  // shed/hut/lake), "dock" (anchor as the conventional docking symbol,
  // tool-for-action, same tier as fix/dig), "dot" (a literal small-circle
  // mark — the word IS the shape, distinct from the rejected symbol-tier
  // color/arrow proxies), "elk" (deer glyph, no dedicated elk glyph, same
  // tier as lamb/colt), "fin" (fish glyph, whole-for-part), "jet"
  // (generic airplane glyph, Unicode has no jet/plane distinction, same
  // tier as truck/sock), "kit" (toolbox as the conventional idiomatic
  // symbol for an unspecified kit, same tier as job->briefcase), "pit"
  // (hole glyph for a ground pit), "pod" (pea-pod direct match), "stem"
  // (herb/leafy-stalk glyph, no dedicated stem glyph), "tin" (canned-food
  // glyph for a metal tin, part-for-whole). Rejected this slice: "wreath"
  // (only available glyph, the rosette/award-medallion symbol, depicts a
  // real unrelated object rather than a door wreath — same defect class
  // as the slice-5 "band" rejection, not a mere disclosure-tier
  // approximation), "big"/"tall" (pure size modifiers with no consistent
  // depictable subject across occurrences), "fell" (leaf vs. sock across
  // its 2 occurrences — depicting one misrepresents the other), "fill"
  // (liquid vs. solid across its 2 occurrences — the only close glyph,
  // pouring liquid, contradicts the block-filling occurrence), "knew"
  // (abstract cognitive verb, no concrete referent), "mammal" (abstract
  // taxonomic category, no single consistent referent and no generic
  // glyph), "miss" (genuine sense split: fail-to-catch-a-bus vs.
  // fail-to-have-a-nap, same class as cut/stamp/cab), "off"/"will" (pure
  // function/modal words, no concrete referent, same class as much/that/
  // with), "top" (genuine sense split: spinning-toy vs. positional upper
  // part, confirmed via CS_06_127/CS_10_120), "desk"/"fig"/"moth"/"twin"
  // (no accurate species/object glyph exists without misrepresentation —
  // fig would borrow another fruit's glyph, moth would borrow butterfly
  // and contradict an explicit nocturnal framing, twin has no dedicated
  // Unicode glyph). See the BACKLOG.md decision log for the full audit
  // trail (design + red-team convergence).
  bob: "👦", bud: "🌱", crumb: "🍞", hall: "🏫", limb: "🪵", pull: "💪",
  rim: "🍵", wring: "💧", wrist: "✋", dock: "⚓", dot: "⚫", dress: "👗",
  elk: "🦌", fin: "🐟", hammer: "🔨", jet: "✈️", kit: "🧰", mug: "☕",
  pit: "🕳️", pod: "🫛", ram: "🐏", stem: "🌿", tin: "🥫",
  // MA-308 full-target-coverage slice 9 (2026-07-23): a 29-word batch from an
  // independent design pass (89 candidates from the freq-1/freq-2 uncovered
  // tail) + an adversarial red-team re-audit of every real corpus occurrence,
  // lifting real mode-a coverage 692->721 (measured). Every one of these 29
  // words has exactly ONE real corpus occurrence (unlike prior slices' ring/
  // cap/top-class rejects), so there is no sense-split-across-occurrences risk
  // for this batch. Clean direct/single-sense words: win, cob, sob, dad.
  // Word-family / glyph reuses with textual support (verb-tense or agent-noun
  // forms of already-mapped base words, or clean synonyms of an already-
  // mapped concept — same tier as the shipped bake/raft/mop precedent, not a
  // sense-collapse): kick/pass->soccer-ball, park/lake/pond->landform, crop
  // (sentence itself says "grow in a pot")/plant->potted-plant, drummer/
  // drum, summer/sun, hummed/hum, swimmer/swim, wrote/write, sum/math,
  // damp/wet, gulp/pop/sip->drink-cup (checked exhaustively: sip's own 4 real
  // occurrences never list pop/gulp as options, and vice versa), rinse/wash,
  // munch/eat, hog/pig (no dedicated hog glyph), jog/sprint, fuzz/fluff,
  // cot/bed (checked: bed and cot never co-occur as options in the same real
  // item), gus (proper name treated as generic-boy noun, same tier as the
  // already-shipped "bob" — structurally identical sentence shape). "claw"
  // is a direct textual match, not a residue: CS_15_114 literally names the
  // subject ("The crab waved its big ___"). "clang" reuses bell's glyph
  // because "bell" is the explicit sound-making subject named in the same
  // sentence (the sanctioned subject-names-the-sound pattern, same class as
  // the documented bee/buzz pairing). "trim" (scissors, tool-for-action,
  // same tier as fix/dig) and "rod" (fishing rod — supported by the "fin"
  // distractor in the same item) round out the batch. "set" is accepted as
  // the sunset sense (its one real occurrence is unambiguous) with a
  // disclosed latent-fragility note: "set" is highly polysemous and this
  // map has no per-item override, so a FUTURE item using a different sense
  // of "set" would silently render the wrong picture — same accepted-residue
  // class as "pen". REJECTED this slice: "hid" (CS_06_078, "The cub ___ in
  // den") — a red-team finding, not a design-pass miss: "hid" is a VERB with
  // no direct object (unlike dig->pickaxe/fix->wrench, which depict a tool
  // the verb is performed WITH), and the only nearby glyph, 🕳️, is already
  // shared by three unrelated NOUN senses (den/crack/pit, "a hole in the
  // ground") — depicting it for "hid" would show the bystander location
  // noun ("den"), not the target verb, the exact defect class MA-308 exists
  // to eliminate. Left unmapped. See BACKLOG.md's decision log for the full
  // audit trail (design + red-team convergence, all 89 candidates).
  win: "🏆", cob: "🌽", kick: "⚽", park: "🏞️", trim: "✂️",
  crop: "🪴", drummer: "🥁", summer: "☀️", hummed: "🎵", swimmer: "🏊",
  wrote: "✏️", can: "🥫", sum: "🔢", damp: "💧", gulp: "🥤",
  rinse: "🧼", munch: "🍽️", clang: "🔔", claw: "🦀", hog: "🐷",
  jog: "🏃", sob: "😭", cot: "🛏️", fuzz: "☁️", gus: "👦",
  dad: "👨", pop: "🥤", rod: "🎣", set: "🌇",

  // MA-308 full-target-coverage slice 10 (2026-07-23): a 25-word batch, all
  // freq-1 in the real uncovered tail (no sense-split-across-occurrences risk
  // for this batch, same as slice 9). An independent design pass proposed 26
  // candidates; an adversarial red-team pass independently re-derived every
  // real corpus occurrence + re-ran the mechanical collision sweep and
  // OVERTURNED one design-pass accept: "twist" (CS_09_086, "The kid can ___
  // the lid") — REJECTED, because the only available glyph, 🔄, is the
  // near-universal digital "refresh/reload/repeat" symbol, a far stronger
  // and actively-competing established meaning than this map's usual
  // tool/body-for-action metaphors — the same "well-known alternate meaning
  // actively misleads" defect class that sank "spill" (worse than "gnaw",
  // which at least has no competing meaning). Reuse-glyph residues (same
  // part-for-whole/prototype/subject-names-the-sound tier as the already-
  // shipped bake/raft/mop/bell/clang precedents, not sense-collapses):
  // "bank" (🏞️ reuses lake/pond/park's landform glyph for a riverbank),
  // "cub" (🐻 reuses bear, no dedicated cub glyph, same tier as lamb/colt),
  // "class" (🏫 reuses hall's school-building glyph), "grass"/"patch" (🌿
  // reuse stem's herb glyph — verified "grass" and "patch" never co-occur
  // as options within the same item, so no same-item collision from
  // sharing one glyph), "lump" (🍞 reuses bread, part-for-whole, same tier
  // as crust/slab), "speck" (⚫ reuses dot's glyph, tiny-mark metaphor),
  // "flock" (🦆 reuses duck, collective-noun-for-member), "throne" (🪑
  // reuses chair, no dedicated throne glyph), "breath" (💨 reuses the
  // draft/puff/fog wind-glyph family), "weight" (🪨 reuses rock,
  // prototype-heavy-object), "creak" (🚪 reuses door — CS_15_111 "The wind
  // made the gate ___" — the sanctioned subject-names-the-sound pattern,
  // same class as bell/clang, bee/buzz), "rest" (😴 reuses nap, direct
  // synonym), "grasp"/"grab" (✊, synonyms of each other), "tug" (💪 reuses
  // pull/lift's bicep glyph), "tap"/"pet"/"rub" (✋ reuse hand/pat's glyph
  // for three distinct hand actions — verified none of the six hand/fist-
  // glyph words above (grasp/grab/tug/tap/pet/rub) ever co-occur as options
  // within the same real item, so no same-item collision), "beg" (🙏
  // conventional pleading-hands symbol, generic idiom not the dog's literal
  // posture). Two clean direct matches: "mad" (😡, same tier as the already-
  // shipped "grin"), "flap" (🎏 the carp-streamer glyph is a real Unicode
  // "flag" that depicts fluttering-in-wind motion itself, the same subject-
  // names-the-motion pattern as creak/bell — flagged for the Cowork live-QA
  // visual-recognition check, since a Western child may not immediately
  // read it as "a flag" without cultural context, same disclosure tier as
  // swat/fluff). Two disclosed residues carrying a documented precedent
  // tension (kept, not silently shipped — see PR body): "yummy" (😋, the
  // "face savoring food" glyph is near-synonymous with the word itself,
  // distinguished from the REJECTED "cute"->🥰 in slice 4 because 🥰 gave
  // zero information about an external adjective while 😋 IS the concept),
  // "nod" (🙂‍↕️, a real, verified Unicode 15.1 ZWJ sequence — "head shaking
  // vertically" — flagged for the Cowork live-QA visual check as a newer
  // glyph unverified in this headless env, same disclosure tier as jar/
  // comb/sigh/swat). Real mode-a target coverage 721->746 (measured, 25
  // accepts — "twist" excluded from the count). Rejected this slice (see
  // BACKLOG.md's decision log for the full audit trail): "land" (no generic
  // touch-down glyph without misrepresenting via an aviation-specific
  // symbol), "nudge" (no push/nudge Unicode glyph), "bit" (only tooth/
  // grimace approximations, same defect class as "gnaw"), "zip" (🤐
  // zipper-mouth-face carries a dominant "keep quiet/secret" slang meaning,
  // same risk class as "twist"), "ink" (the only candidate glyph depicts
  // the writing tool, not the ink substance, same defect class as "gnaw"),
  // "wag" (the only nearby glyph would depict the bystander subject noun
  // "pup", not the target verb, same defect class as slice-9's "hid"),
  // "sub" (no dedicated submarine glyph; reusing "ship" would contradict
  // the item's own distinguishing reasoning), "tab"/"pad" (no adequate
  // unambiguous glyph exists), "bad" (spoiled food — no glyph that doesn't
  // misread as "sick" rather than "spoiled"), "rip"/"fling"/"toss"/"wreck"
  // (no adequate glyph without reaching for a violence-flagged symbol, same
  // tier as the held-back "hit"/"bang"), "spit" (hygiene-sensitive, same
  // tier as "spill"), "stuck"/"jammed"/"snug"/"soft"/"fun"/"knack"
  // (abstract states/modifiers with no consistent depictable referent,
  // same class as "honest"/"calm"), "ramp"/"comma"/"blip"/"slim"/"head"
  // (no adequate single-sense glyph). Also confirmed reject-as-a-block: the
  // full ms.read.16 Tier-2 academic-vocabulary word set (examine, gather,
  // observe, predict, compare, similar, different, describe, explain,
  // identify, classify, sort, measure, estimate, evidence, reason, result,
  // method, create, combine, separate, include, exclude, locate, select,
  // arrange, notice, require, respond, connect, reduce, increase, support,
  // define, summarize, sequence, contrast, conclusion, detail, category,
  // example, process, solution, effect, cause, investigate, record, revise,
  // focus, accurate, possible, visible, source, demonstrate, illustrate,
  // beneath, beyond, gigantic, tremendous) — abstract cognitive verbs/nouns
  // with no single concrete depictable referent, same class as the already-
  // rejected "knew"/"mammal". Full independent corpus re-verification
  // (every occurrence read by both the design and red-team passes, not
  // sampled) + a mechanical collision sweep (0 real collisions, incl.
  // against the reused glyphs) backs every entry below.
  bank: "🏞️", cub: "🐻", cart: "🛒", class: "🏫", grass: "🌿",
  patch: "🌿", lump: "🍞", speck: "⚫", flock: "🦆", flap: "🎏",
  throne: "🪑", breath: "💨", weight: "🪨", creak: "🚪", yummy: "😋",
  mad: "😡", rest: "😴", grasp: "✊", grab: "✊", tug: "💪",
  tap: "✋", pet: "✋", rub: "✋", beg: "🙏", nod: "🙂‍↕️",
  // MA-308 full-target-coverage slice 11 (2026-07-23): after 10 rounds the
  // uncovered tail is almost entirely words ALREADY audited-and-rejected in
  // slices 2-10 for a genuine sense-split or a missing glyph — a real-corpus
  // re-scan found only ONE fresh clean word ("high", below). Rather than stop
  // at that thin yield, this slice introduces a PER-ITEM override
  // (TARGET_WORD_ITEM_EXCLUSIONS below + the itemId param on
  // pickTargetEmoji) so a previously-rejected word's MAJORITY-sense real
  // occurrences can be reclaimed while the specific minority-sense item(s)
  // that sank it stay excluded. This does NOT reopen every prior rejection —
  // only words rejected for a genuine SENSE-SPLIT (a minority of real
  // occurrences using a different sense than the rest) are eligible; words
  // rejected for having NO adequate glyph even for their single shared sense
  // (gnaw, band, doll — re-verified this slice, still 100% one-sense each,
  // still no glyph that fits) or for a connotation/category-mismatch
  // independent of sense (thumb -> 👍 is the "approval gesture" glyph, a
  // category mismatch on EVERY occurrence, same defect class as the already-
  // rejected "cute"->🥰; also re-confirmed for cut/stamp/cab/snap/drop/print,
  // each a genuine near-even split with no reclaimable majority) get zero
  // benefit from the mechanism and stay excluded. An independent design pass
  // + an adversarial red-team pass both independently re-read EVERY real
  // occurrence of all 6 accepted words (ride, rode, chop, tube, glass, wrap)
  // and converged with zero citation discrepancies (unusual for this series —
  // most prior slices found at least one). Accepted, with their excluded
  // item(s) and the exact reasoning:
  //   - ride (10 occ) + rode (9 occ) -> 🚲: 19 real occurrences share the
  //     generic bike-riding sense; 3 explicitly name a DIFFERENT vehicle/
  //     animal (CS_12_063 "We will ___ the bus", CS_12_069 "Dad ___ in the
  //     car", CS_12_070 "She ___ the mule at the farm") which a bicycle
  //     emoji would misdepict — excluded per-item, the other 16 reclaimed.
  //   - chop (8 occ) -> 🪓: 7/8 are wood-chopping-with-an-axe; CS_11_055
  //     ("The cook will ___ the ham") is a food-prep context where an axe is
  //     implausible — excluded, the other 7 reclaimed.
  //   - tube (8 occ) -> 🧴: 6/8 are squeeze-tube (toothpaste/paint/gel)
  //     contexts; CS_12_097_v2 ("A ___ can hold sand") and CS_12_100_v2
  //     ("The map was in the ___") describe a RIGID mailing/storage tube a
  //     squeeze-bottle glyph would contradict — excluded, the other 6
  //     reclaimed.
  //   - glass (3 occ) -> 🥛: 2/3 are the literal "glass of milk" vessel
  //     sense; CS_14_068 ("The cup was made of clear ___") tests the
  //     MATERIAL sense instead — excluded, the other 2 reclaimed.
  //   - wrap (3 occ) -> 🎁: 2/3 are gift-wrapping; CS_15_035 ("We will ___
  //     the sandwich in foil") is a food-wrap context a gift-box emoji would
  //     misdepict — excluded, the other 2 reclaimed.
  // Plus one clean new word with no exclusion needed: "high" (2/2 occ, both
  // the literal spatial-height sense: CS_15_051 "The sun was ___ above the
  // trees", CS_15_100 "The plane flew very ___") -> ⬆️, the same direct
  // symbol-tier depiction already established for other adjective/abstract-
  // leaning words in this map (wrong->❌, dim->🌑). Net real coverage this
  // slice: 746->781 (measured: 9+7+7+6+2+2+2=35), zero target/distractor
  // emoji collisions across the full real launch-safe pool (mechanically
  // swept, itemId-aware). "cut" (5 occ, 3 wound-noun/2 slicing-verb) was
  // investigated as a candidate for the same override treatment (a bandage
  // glyph 🩹 fits the majority wound sense) but DEFERRED, not shipped — the
  // question of depicting a minor injury (even non-graphically) is a
  // distinct content-tone call from this slice's material/vehicle/tool
  // sense-splits and deserves its own dedicated decision, not a bundled
  // afterthought; left for a future slice.
  ride: "🚲", rode: "🚲", chop: "🪓", tube: "🧴", glass: "🥛",
  wrap: "🎁", high: "⬆️",

  // MA-308 full-target-coverage slice 12 (2026-07-23): after slice 11 drained
  // the sense-split/override tail to zero fresh word-level yield (an
  // independent design + red-team re-audit of made/line/bump/miss — the only
  // uncovered words with no rejection comment above — confirmed none clears
  // the reclaim-ratio floor the shipped overrides established, 67-87.5%;
  // bump's swelling-noun sense is additionally injury-adjacent, same
  // deferral tier as "cut"), the red-team pass swept the frequency-1 tail
  // for words genuinely never examined by any prior slice (as opposed to
  // the ms.read.16 academic-vocabulary block-reject or the many named single-
  // word rejects already covering the rest of that tail): limp, silk, fed,
  // tip, fit. Four reject on the established "no adequate glyph" / "depicts
  // the bystander, not the target" grounds (limp/silk: no dedicated glyph;
  // fed: every candidate depicts the food or the bystander "hen", not the
  // feeding action; fit: reusing lid's own glyph depicts the bystander
  // subject already named in the text, and a checkmark carries an even
  // stronger competing "correct answer" meaning than the reload arrows that
  // sank "twist"). One clears the bar: "tip" (CS_06_099, "Vic can ___ the
  // cup", single real occurrence, no sense-split possible) -> 🫗 (Pouring
  // Liquid, Unicode 13.0) depicts the RESULT of tipping a cup, the same
  // result-for-action metaphor tier already shipped for "wring"->💧; flagged
  // for the Cowork live-QA visual check as a newer glyph unverified in this
  // headless env, same disclosure tier as jar/comb/sigh/swat/nod. Net real
  // coverage this slice: 781->782 (measured), zero target/distractor emoji
  // collisions (mechanically swept).
  tip: "🫗",

  // MA-492 Slice 5a (2026-08-13): a full re-audit of the remaining 162
  // distinct no-support words (314 items, re-measured live — the census
  // moved from the design spec's 319 to 314 since MA-308 slice 12, per
  // MA-426's held-item edits) against MA-121's own accept/reject test.
  // RESULT: zero new emoji accepts. Every top-frequency word the design
  // spec names (made, line, cute, chin, much, thin, that, ring, long, with)
  // was ALREADY reviewed-and-rejected across the 12 emoji rounds above —
  // re-checking each against the real corpus found no new evidence to
  // overturn any of those verdicts (honesty over count: this pass does not
  // force an accept). The only two words in the full 162-word tail with NO
  // prior mention anywhere in this file (i.e. genuinely never examined by
  // any earlier slice) were found by a mechanical cross-reference sweep and
  // are recorded here for the first time:
  //   - "flop" (CS_EXP2_182, "Jan can ___ on the soft mat", 1 real
  //     occurrence, single sense — falling comfortably) — REJECTED for
  //     emoji (no adequate Unicode glyph depicts this specific motion
  //     without borrowing an unrelated glyph), but concrete/depictable —
  //     routed to the AI-image pipeline instead (D3, MA-492 Slice 5b, see
  //     scripts/aiImages/csWordImageTargets.mjs).
  //   - "organize" (CS_16_035, "Put the books on the shelf to ___ them.")
  //     — REJECTED, same class as the already-block-rejected ms.read.16
  //     academic-vocabulary set just above (abstract cognitive verb, no
  //     single concrete depictable referent in any medium).
  // The remaining 160 words' verdicts are UNCHANGED from their prior
  // slices' reasoning above; scripts/aiImages/csWordImageTargets.mjs is
  // the full, current, machine-checked committed reference for what
  // happens to every one of the 162 words next (52 distinct words / 97
  // items routed to the AI-image pipeline, the rest permanently exempt —
  // see that file's header for the complete count reconciliation and
  // noHintExemptionsGuard.mjs for the live re-measurement that keeps it
  // honest).
};

// MA-308 full-target-coverage slice 11: per-item exclusions for words above
// whose majority sense is mapped but whose OWN minority-sense real
// occurrences must still render nothing (falling back to the mode-a
// bystander scan, same as an unmapped word) rather than the majority-sense
// emoji. Keyed by word, value = the Set of real controlledSentences ids
// whose occurrence uses a different, non-depictable sense. See the map
// comment above for the citation + reasoning behind each entry.
const TARGET_WORD_ITEM_EXCLUSIONS = {
  ride: new Set(["CS_12_063"]),
  rode: new Set(["CS_12_069", "CS_12_070"]),
  chop: new Set(["CS_11_055"]),
  tube: new Set(["CS_12_097_v2", "CS_12_100_v2"]),
  glass: new Set(["CS_14_068"]),
  wrap: new Set(["CS_15_035"]),
  // MA-592 emoji-map slice (2026-09-13) — the SAME mechanism, now applied
  // to encodingWords sentence_completion items (D13 wires them through
  // pickTargetEmoji(correctAnswer, item.id) too). Sweep 4 read the served
  // PIC glyphs exactly as the mapper resolves them; a RED pass over all 38
  // raw-resolving launch_safe items (docs/build/reports/MA-592-emoji-map-
  // red-2026-09-13.md) confirmed the wrong-sense set below. Each entry is a
  // WRONG-SENSE or WRONG-REFERENT serve for that item alone — the word's
  // glyph stays right for its other occurrences, so the word-level map is
  // untouched (sleep is the one exception: remapped above, no consumer of
  // the bear sense exists).
  //   - "right" -> ➡️ for the CORRECTNESS sense: HFW_EXP3_sc_057 "That is
  //     the ___ way." HFW_EXP3_sc_027 "Turn ___ at the sign." keeps ➡️.
  //     controlledSentences CS_15_050 ("That answer is ___ because three
  //     plus two is five.") had the SAME defect in this lane (the image
  //     lane already excluded it — scripts/aiImages/csWordImageTargets.mjs,
  //     Fable ruling; MA-511's filed follow-up). MA-645 (2026-09-13) closes
  //     it: excluded below and REFUSED a gloss (prompt-tautology, the frame's
  //     because-clause explains the word) — the gloss route deadlocks: a gloss
  //     only enters the narratable denominator once this exclusion serves,
  //     and narration-regenerate.yml refuses a content_ref touching src/.
  //   - "small" -> 🐭 beside HFW_EXP3_sc_095 "The bug is very ___." — a
  //     mouse next to a sentence about a bug is a wrong-referent picture
  //     (the 🐭-for-"wren" class this file's MODIFIER_TIER note already
  //     names) and invites "mouse", not "small".
  //   - "full" -> 🍱 beside HFW_EXP3_sc_073 "My cup is ___." and
  //     HFW_EXP3_sc_110 "The bag is ___ of toys." — a bento box depicts
  //     food, not fullness of a cup or a toy bag (sweep 4 :475/:507).
  //   - "sing" -> 🎤 beside HFW_EXP3_sc_028 "Birds ___ at dawn." — a
  //     microphone is a human-performance symbol; wrong referent for birds
  //     (sweep 4 :441). The controlledSentences sing items ("The kids can
  //     ___ a song.") keep 🎤.
  //   - "plant" -> 🪴 beside HFW_EXP3_sc_147 "I will ___ a seed." — the
  //     potted-plant NOUN glyph for the VERB answer, the same noun/verb
  //     homograph class as HOMOGRAPH_EXCLUDED_ANSWERS' "can".
  // Each withdrawal flips the item to honest no-hint (D14 residue); the
  // noHintExemptions.json sentence_completion-function-word-residue count
  // and the picScaffoldAnswerSafetyTest / readingHintContentAudit pins move
  // in the same diff, per noHintExemptionsGuard.mjs.
  // MA-645 (2026-09-13) adds CS_15_050 — the same correctness sense; the
  // item is honestly no-hint (gloss refused, see csGlossRefusals.json).
  // CS_15_099 and sc_027 (direction) keep ➡️.
  right: new Set(["HFW_EXP3_sc_057", "CS_15_050"]),
  small: new Set(["HFW_EXP3_sc_095"]),
  full: new Set(["HFW_EXP3_sc_073", "HFW_EXP3_sc_110"]),
  sing: new Set(["HFW_EXP3_sc_028"]),
  plant: new Set(["HFW_EXP3_sc_147"]),
};

// MA-308 mode (a): a DIRECT lookup on the item's own target/answer word —
// never a template scan. Used only by ScaffoldPIC for decoding-cloze items
// (controlledSentences), where the founder ruled the picture's job is to
// depict the TARGET's meaning-in-sentence (a fluent reader's context clue),
// not to avoid it — the assessed skill is decoding the letters, not
// guessing the word from the picture. Returns null (never a guess) when the
// word has no mapped emoji. Checks TARGET_ONLY_EMOJI_BY_WORD first (the
// slice-2 coverage expansion above), then falls back to the shared
// EMOJI_BY_TEMPLATE_WORD (preserves every already-shipped word, e.g. "bike",
// "web", byte-for-byte).
//
// MA-308 slice 11: optional second `itemId` param — when the real
// controlledSentences item id is a documented exception in
// TARGET_WORD_ITEM_EXCLUSIONS for this word, returns null (the item's own
// minority sense is not depicted) even though the word is otherwise mapped.
// A missing/unrecognized itemId is always safe/inert (no exclusion applies,
// same as calling with one argument) — this can never suppress a valid
// depiction for the wrong item.
export function pickTargetEmoji(word, itemId) {
  const key = String(word ?? "").trim().toLowerCase();
  if (itemId != null && TARGET_WORD_ITEM_EXCLUSIONS[key]?.has(itemId)) return null;
  const emoji = TARGET_ONLY_EMOJI_BY_WORD[key] || EMOJI_BY_TEMPLATE_WORD[key];
  return emoji ? { word: key, emoji } : null;
}

// MA-308 style fix: no card behind the depiction — the founder's live QA
// flagged the rounded, drop-shadowed SceneFrame box as reading like a
// foreign "whitish card" over the app's warm cream canvas. Render the
// emoji directly, transparent framing. (The 18 hand-drawn SCENES keep
// SceneFrame — they were authored assuming its padded square card and are
// out of MA-308's scope; only the deterministic emoji fallback changes.)
function EmojiScene({ emoji, size = 220 }) {
  return (
    <div style={{ width: "100%", maxWidth: size, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center", aspectRatio: "1 / 1" }}>
      <span style={{ fontSize: Math.round(size * 0.55), lineHeight: 1 }}>{emoji}</span>
    </div>
  );
}

export default function SceneIllustration({ scene, objectType, count, size, template, emoji, depictionMode }) {
  if (scene === "counted_objects") {
    return <CountedObjects objectType={objectType} count={count}/>;
  }
  const Scene = SCENES[scene];
  if (Scene) return <Scene size={size}/>;

  // MA-308 mode (a), full-target-coverage (architectural slice, closes
  // slices-remaining to none): the caller already resolved `emoji` via
  // pickTargetEmoji (a direct answer lookup). NO bystander fallback here —
  // that WAS the original bug (a bystander depiction), and MA-119's
  // cs-pic-empty map-time gate (contentToEngineItem.ts) already withdraws
  // PIC from `supports` whenever the target has no mapped emoji, so this
  // branch is never reached with a falsy `emoji` on a real served item; the
  // `null` return here is defense-in-depth only. Every mode-(a) item now
  // either depicts its TARGET or is honestly absent — 0 bystander-only
  // depictions, exactly MA-308's §3d acceptance text.
  if (depictionMode === "targetWord") {
    if (emoji) return <EmojiScene emoji={emoji} size={size}/>;
    return null;
  }

  // Fallback (mode (b), unchanged): explicit emoji prop (engine-supplied,
  // vetted), then template subject. Never the answer.
  const fallbackEmoji = emoji || pickEmojiFromTemplate(template);
  if (fallbackEmoji) return <EmojiScene emoji={fallbackEmoji} size={size}/>;
  return null;
}
